/**
 * Platform design tokens — the only place a Platform colour literal is written.
 * `utilifeed.theme.ts` consumes these and holds no hex of its own.
 *
 * `accent` and `graph` share seven names (red, green, blue, teal, yellow, purple,
 * pink) but hold different values: accent is saturated, for themed UI; graph is
 * muted, for chart series. Only `orange` matches, because both resolve to
 * `brand.secondary`. Merging the two sets would restyle every chart.
 */

const brand = {
  /** Primary brand colour (blue). */
  primary: { main: "#315085", dark: "#22375C", light: "#657CA4" },
  /** Alternate brand colour (orange). */
  secondary: { main: "#DB742B", dark: "#A45720", light: "#E49760" },
} as const;

/** Themed UI colours, fed through `palette.augmentColor` → `palette.<name>`. */
const accent = {
  red: { main: "#C44D4B", dark: "#933A38", light: "#D37A78" },
  green: { main: "#5BCF65", dark: "#449B4C", light: "#84DB8C" },
  blue: { main: "#5F84C4", dark: "#476393", light: "#87A3D3" },
  teal: { main: "#5AC4CE", dark: "#44939B", light: "#84D3DB" },
  yellow: { main: "#D9C94C", dark: "#A39739", light: "#E3D779" },
  purple: { main: "#784CD4", dark: "#5A399F", light: "#9A79DF" },
  pink: { main: "#D44C9A", dark: "#9F3974", light: "#DF79B4" },
  /** `palette.orange` is the brand secondary — referenced, not copied, so they can't drift. */
  orange: brand.secondary,
} as const;

/** Chart series colours → `palette.graph.<name>`. Muted counterparts to `accent`. */
// TODO: fold into the MUI palette. Deferred because ~49 call sites read `palette.graph.*`,
// so collapsing it into `accent` would change the colours every chart renders.
const graph = {
  blue: "#4A88BB",
  lightblue: "#a4c3dd",
  lightPink: "#dda4d4",
  pink: "#BB4AAA",
  darkPink: "#bc5090",
  lightTeal: "#a4dbdd",
  teal: "#4AB7BB",
  yellow: "#BBAE4A",
  darkYellow: "#d9bb3c",
  purple: "#6D4ABB",
  lightGreen: "#a8dda4",
  green: "#53bb4a",
  red: "#BB4A4A",
  lightOrange: "#ecb893",
  orange: "#db742b",
} as const;

const semantic = {
  error: "#bb4a4a",
  /**
   * Differs from `graph.green` (#53bb4a) by one hex digit, so it is probably a typo.
   * Left alone because correcting it is a visible colour change.
   */
  success: "#53B84A",
  warning: "#FFC107",
  info: "#4AB7BB",
} as const;

const border = {
  primary: "#dbe0e4",
  light: "#eee",
  divider: { main: "#2A4471", dark: "#1D2F4E" },
} as const;

const background = {
  default: "#f5f5f5",
  paper: "#fff",
  selected: "#ebebeb",
  hover: "#f5f5f5",
} as const;

const text = { primary: "#424242" } as const;

const font = {
  main: "Roboto",
  alt: "Montserrat",
  condensed: "Roboto Condensed",
} as const;

export const utfTokens = { brand, accent, graph, semantic, border, background, text, font };

/** Keys of the themed UI set → `theme.palette[AccentKey]`. */
export type AccentKey = keyof typeof accent;

/** Keys of the chart series set → `theme.palette.graph[GraphKey]`. */
export type GraphKey = keyof typeof graph;

/** MUI's built-in semantic slots — distinct from `AccentKey`. `MapView` maps values onto these. */
export type SemanticColorKey = "primary" | "secondary" | "error" | "warning" | "info" | "success";
